/**
 * Mode-agnostic loan threshold wire fields (Thresholds_*).
 *
 * In-app source of truth: Thresholds_* (MLA form + deal form fields).
 * Write path: emit Thresholds_* only (backend still accepts legacy *LTV on input).
 * Read path: prefer Thresholds_*, fall back to legacy *LTV for older blobs, then
 * strip *LTV from in-memory state after hydrate.
 * CollateralizationMode alone interprets whether values are LTV or collateralization %.
 */

export interface LoanThresholdValues {
  minimum?: string | undefined;
  marginCall?: string | undefined;
  liquidation?: string | undefined;
  collateralRelease?: string | undefined;
}

/** Thresholds_* keys for loan deal / MLA write payloads */
export interface LoanThresholdWireFields {
  Thresholds_Minimum?: string;
  Thresholds_MarginCall?: string;
  Thresholds_Liquidation?: string;
  Thresholds_CollateralRelease?: string;
}

export type LoanThresholdPayloadFields = {
  MinimumLTV?: string | null;
  MarginCallLTV?: string | null;
  LiquidationLTV?: string | null;
  CollateralReleaseLTV?: string | null;
  Thresholds_Minimum?: string | null;
  Thresholds_MarginCall?: string | null;
  Thresholds_Liquidation?: string | null;
  Thresholds_CollateralRelease?: string | null;
};

function present(value: string | null | undefined): string | undefined {
  if (value == null || value === '') {
    return undefined;
  }
  return value;
}

/** Prefer Thresholds_* when set; otherwise fall back to legacy *LTV. */
export function resolveLoanThreshold(
  thresholdsValue: string | null | undefined,
  legacyLtvValue: string | null | undefined
): string | undefined {
  return present(thresholdsValue) ?? present(legacyLtvValue);
}

/**
 * Write threshold levels as Thresholds_* keys only.
 * Omits a level entirely when the value is absent/empty.
 */
export function writeLoanThresholds(values: LoanThresholdValues): LoanThresholdWireFields {
  const out: LoanThresholdWireFields = {};

  const minimum = present(values.minimum);
  if (minimum !== undefined) {
    out.Thresholds_Minimum = minimum;
  }

  const marginCall = present(values.marginCall);
  if (marginCall !== undefined) {
    out.Thresholds_MarginCall = marginCall;
  }

  const liquidation = present(values.liquidation);
  if (liquidation !== undefined) {
    out.Thresholds_Liquidation = liquidation;
  }

  const collateralRelease = present(values.collateralRelease);
  if (collateralRelease !== undefined) {
    out.Thresholds_CollateralRelease = collateralRelease;
  }

  return out;
}

/** Resolve each threshold level from a payload that may have Thresholds_* and/or *LTV. */
export function resolveLoanThresholdsFromPayload(payload: LoanThresholdPayloadFields): LoanThresholdValues {
  return {
    minimum: resolveLoanThreshold(payload.Thresholds_Minimum, payload.MinimumLTV),
    marginCall: resolveLoanThreshold(payload.Thresholds_MarginCall, payload.MarginCallLTV),
    liquidation: resolveLoanThreshold(payload.Thresholds_Liquidation, payload.LiquidationLTV),
    collateralRelease: resolveLoanThreshold(payload.Thresholds_CollateralRelease, payload.CollateralReleaseLTV),
  };
}

function stripLegacyLtvKeys<T extends LoanThresholdPayloadFields>(
  agreement: T
): Omit<T, 'MinimumLTV' | 'MarginCallLTV' | 'LiquidationLTV' | 'CollateralReleaseLTV'> {
  const { MinimumLTV: _min, MarginCallLTV: _mc, LiquidationLTV: _liq, CollateralReleaseLTV: _rel, ...rest } = agreement;
  return rest;
}

/**
 * Resolve prefer-new into Thresholds_* form SoT and strip legacy *LTV.
 * MLA forms bind Thresholds_* only after hydrate.
 */
export function hydrateMlaAgreementThresholds<T extends LoanThresholdPayloadFields>(agreement: T): T {
  const resolved = resolveLoanThresholdsFromPayload(agreement);
  const thresholds = writeLoanThresholds({
    minimum: resolved.minimum,
    marginCall: resolved.marginCall,
    liquidation: resolved.liquidation,
    collateralRelease: resolved.collateralRelease,
  });

  return {
    ...stripLegacyLtvKeys(agreement),
    ...thresholds,
  } as T;
}

/**
 * Prepare MLA agreement for save: emit Thresholds_* from form SoT only.
 * Never sends legacy *LTV keys.
 */
export function withMlaThresholds<T extends LoanThresholdPayloadFields>(agreement: T): T {
  const thresholds = writeLoanThresholds({
    minimum: present(agreement.Thresholds_Minimum),
    marginCall: present(agreement.Thresholds_MarginCall),
    liquidation: present(agreement.Thresholds_Liquidation),
    collateralRelease: present(agreement.Thresholds_CollateralRelease),
  });

  const {
    MinimumLTV: _min,
    MarginCallLTV: _mc,
    LiquidationLTV: _liq,
    CollateralReleaseLTV: _rel,
    Thresholds_Minimum: _tm,
    Thresholds_MarginCall: _tmc,
    Thresholds_Liquidation: _tl,
    Thresholds_CollateralRelease: _tcr,
    ...rest
  } = agreement;

  return {
    ...rest,
    ...thresholds,
  } as T;
}
